import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";

export interface ProfileVisibility {
  user_id: string;
  visible_to_unverified: boolean;
  visible_to_other_apps: boolean;
  visible_in_search: boolean;
  hide_age_from_strangers: boolean;
  hide_distance_from_strangers: boolean;
  blur_photos_until_match: boolean;
  updated_at: string;
}

const DEFAULTS: Omit<ProfileVisibility, "user_id" | "updated_at"> = {
  visible_to_unverified: true,
  visible_to_other_apps: true,
  visible_in_search: true,
  hide_age_from_strangers: false,
  hide_distance_from_strangers: false,
  blur_photos_until_match: false,
};

export function useProfileVisibility() {
  const { user } = useAuth();
  const [vis, setVis] = useState<ProfileVisibility | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setVis(null);
      setLoading(false);
      return;
    }
    const { data } = await (supabase as any)
      .from("profile_visibility")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();
    setVis(
      (data as ProfileVisibility | null) ?? {
        user_id: user.id,
        ...DEFAULTS,
        updated_at: new Date().toISOString(),
      },
    );
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const update = useCallback(
    async (patch: Partial<Omit<ProfileVisibility, "user_id" | "updated_at">>) => {
      if (!user) return { error: "not authenticated" };
      const { error } = await (supabase as any)
        .from("profile_visibility")
        .upsert({ user_id: user.id, ...DEFAULTS, ...vis, ...patch, updated_at: new Date().toISOString() });
      if (!error) await refresh();
      return { error: error?.message ?? null };
    },
    [user, vis, refresh],
  );

  return { visibility: vis, loading, update, refresh };
}
